import { SupabaseClient } from '@supabase/supabase-js';
import { DocumentRepository } from '../repositories/document.repository';
import { ProjectUserRepository } from '../repositories/project-user.repository';
import { DocumentDTO } from '../dtos';
import { DocumentMapper } from '../mappers/document.mapper';

export interface ImportDiscordRequest {
  discordMessageId: string;
  discordChannelId: string;
  projectId: string;
  discordApiToken?: string; // Optional for backward compatibility
}

export interface ImportDiscordResponse {
  success: boolean;
  document?: DocumentDTO;
  message?: string;
  error?: string;
}

export interface DiscordMessage {
  id: string;
  content: string;
  author: {
    id: string;
    username: string;
    discriminator: string;
    avatar?: string;
  };
  channel_id: string;
  guild_id?: string;
  timestamp: string;
  edited_timestamp?: string;
  thread?: {
    id: string;
    name: string;
  };
  attachments?: DiscordAttachment[];
  embeds?: DiscordEmbed[];
  reactions?: DiscordReaction[];
  reference?: {
    message_id: string;
    channel_id: string;
    guild_id?: string;
  };
}

export interface DiscordAttachment {
  id: string;
  filename: string;
  size: number;
  url: string;
  proxy_url: string;
  content_type?: string;
  description?: string;
  height?: number;
  width?: number;
}

export interface DiscordEmbed {
  title?: string;
  type?: string;
  description?: string;
  url?: string;
  timestamp?: string;
  color?: number;
  footer?: {
    text: string;
    icon_url?: string;
  };
  image?: {
    url: string;
    height?: number;
    width?: number;
  };
  thumbnail?: {
    url: string;
    height?: number;
    width?: number;
  };
  author?: {
    name?: string;
    url?: string;
    icon_url?: string;
  };
  fields?: {
    name: string;
    value: string;
    inline?: boolean;
  }[];
}

export interface DiscordReaction {
  count: number;
  me: boolean;
  emoji: {
    id?: string;
    name: string;
  };
}

export interface DiscordChannel {
  id: string;
  type: number;
  guild_id?: string;
  name?: string;
  topic?: string;
  nsfw?: boolean;
  position?: number;
  parent_id?: string;
}

export interface SearchDiscordMessagesRequest {
  discordApiToken?: string; // Optional - will use OAuth token if not provided
  projectId: string;
  query?: string;
  channelId?: string;
  limit?: number;
  before?: string;
  after?: string;
}

export interface SearchDiscordMessagesResponse {
  success: boolean;
  messages?: DiscordMessage[];
  hasMore?: boolean;
  nextCursor?: string;
  error?: string;
}

export interface DiscordIntegration {
  id: string;
  userId: string;
  projectId: string;
  guildId: string;
  guildName?: string;
  authorizedUserId?: string;
  createdAt: string;
  updatedAt: string;
}

export interface DiscordOAuthResponse {
  success: boolean;
  authUrl?: string;
  integration?: {
    id: string;
    guild_id: string;
    guild_name?: string;
    bot_user_id?: string;
    created_at: string;
  };
  message?: string;
  error?: string;
}

export class DiscordService {
  private documentRepository: DocumentRepository;
  private projectUserRepository: ProjectUserRepository;

  constructor(private supabase: SupabaseClient) {
    this.documentRepository = new DocumentRepository(supabase);
    this.projectUserRepository = new ProjectUserRepository(supabase);
  }

  /**
   * Discord 메시지를 문서로 가져오기
   */
  async importFromDiscord(
    request: ImportDiscordRequest,
    userId: string
  ): Promise<ImportDiscordResponse> {
    try {
      // 1. 사용자가 프로젝트 멤버인지 확인
      const projectUser = await this.projectUserRepository.findByProjectAndUser(
        request.projectId,
        userId
      );
      
      if (!projectUser || projectUser.role === 'viewer') {
        return {
          success: false,
          error: '문서를 생성할 권한이 없습니다.'
        };
      }

      // 2. OAuth 토큰 가져오기 (API 키가 없는 경우)
      let discordApiToken = request.discordApiToken;
      if (!discordApiToken) {
        const accessToken = await this.getDiscordAccessToken(userId, request.projectId);
        if (!accessToken) {
          return {
            success: false,
            error: 'Discord 연동이 필요합니다. 먼저 Discord와 연동해주세요.'
          };
        }
        discordApiToken = accessToken;
      }

      // 3. Get user JWT token
      const { data: { session } } = await this.supabase.auth.getSession();
      if (!session?.access_token) {
        return {
          success: false,
          error: 'Authentication required - no valid session found'
        };
      }

      // 4. Edge Function 호출
      const { data, error } = await this.supabase.functions.invoke('import-discord', {
        body: {
          discordMessageId: request.discordMessageId,
          discordChannelId: request.discordChannelId,
          projectId: request.projectId,
          discordApiToken,
        },
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (error) {
        return {
          success: false,
          error: error.message || 'Discord 메시지 가져오기에 실패했습니다.'
        };
      }

      // 4. 응답 처리
      if (data?.success && data?.document) {
        // Edge Function에서 생성된 문서를 조회
        const document = await this.documentRepository.findById(data.document.id);
        
        if (!document) {
          return {
            success: false,
            error: '문서 생성은 성공했으나 조회에 실패했습니다.'
          };
        }

        return {
          success: true,
          document: DocumentMapper.toDTO(document),
          message: data.message || 'Discord 메시지를 성공적으로 가져왔습니다.'
        };
      } else {
        return {
          success: false,
          error: data?.error || '알 수 없는 오류가 발생했습니다.'
        };
      }
    } catch (error) {
      console.error('Error importing from Discord:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Discord 메시지 가져오기에 실패했습니다.'
      };
    }
  }

  /**
   * Discord 메시지 URL에서 메시지 ID와 채널 ID 추출
   */
  extractMessageInfoFromUrl(url: string): { messageId: string; channelId: string; guildId?: string } | null {
    try {
      // Discord 메시지 URL 형식 처리
      // https://discord.com/channels/[guild_id]/[channel_id]/[message_id]
      const match = url.match(/discord\.com\/channels\/(\d+)\/(\d+)\/(\d+)/);
      if (match) {
        const [, guildId, channelId, messageId] = match;
        return { guildId, channelId, messageId };
      }

      return null;
    } catch {
      return null;
    }
  }

  /**
   * Discord Bot 토큰 유효성 검증
   */
  validateDiscordToken(token: string): boolean {
    // Discord 토큰은 일반적으로 길이가 59자이고 특정 패턴을 따름
    return /^[A-Za-z0-9._-]{59,}$/.test(token);
  }

  /**
   * Discord 메시지 검색
   */
  async searchDiscordMessages(
    request: SearchDiscordMessagesRequest,
    userId: string
  ): Promise<SearchDiscordMessagesResponse> {
    try {
      // Get user JWT token
      const { data: { session } } = await this.supabase.auth.getSession();
      if (!session?.access_token) {
        return {
          success: false,
          error: 'Authentication required - no valid session found'
        };
      }

      // Edge Function을 통해 검색 수행
      const { data, error } = await this.supabase.functions.invoke('search-discord', {
        body: {
          query: request.query || '',
          channelId: request.channelId,
          projectId: request.projectId,
          limit: request.limit || 50,
          before: request.before,
          after: request.after,
        },
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (error) {
        return {
          success: false,
          error: error.message || 'Discord 메시지 검색에 실패했습니다.'
        };
      }

      if (!data || !data.success) {
        return {
          success: false,
          error: data?.error || 'Discord 메시지 검색에 실패했습니다.'
        };
      }

      return {
        success: true,
        messages: data.messages || [],
        hasMore: data.hasMore,
        nextCursor: data.nextCursor,
      };
    } catch (error) {
      console.error('Error searching Discord messages:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Discord 메시지 검색에 실패했습니다.'
      };
    }
  }

  /**
   * Discord OAuth 인증 시작
   */
  async initiateDiscordOAuth(
    userId: string,
    projectId: string
  ): Promise<DiscordOAuthResponse> {
    try {
      // Get user JWT token
      const { data: { session } } = await this.supabase.auth.getSession();
      if (!session?.access_token) {
        return {
          success: false,
          error: 'Authentication required - no valid session found'
        };
      }

      const { data, error } = await this.supabase.functions.invoke('discord-oauth', {
        body: {
          action: 'authorize',
          projectId,
        },
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (error) {
        return {
          success: false,
          error: error.message || 'Discord OAuth 시작에 실패했습니다.'
        };
      }

      return {
        success: true,
        authUrl: data.authUrl,
      };
    } catch (error) {
      console.error('Error initiating Discord OAuth:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Discord OAuth 시작에 실패했습니다.'
      };
    }
  }

  /**
   * Discord OAuth 콜백 처리
   */
  async handleDiscordOAuthCallback(
    code: string,
    state: string
  ): Promise<DiscordOAuthResponse> {
    try {
      // Get user JWT token
      const { data: { session } } = await this.supabase.auth.getSession();
      if (!session?.access_token) {
        return {
          success: false,
          error: 'Authentication required - no valid session found'
        };
      }

      const { data, error } = await this.supabase.functions.invoke('discord-oauth', {
        body: {
          action: 'token',
          code,
          state,
        },
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (error) {
        return {
          success: false,
          error: error.message || 'Discord OAuth 콜백 처리에 실패했습니다.'
        };
      }

      return {
        success: true,
        integration: data.integration,
        message: data.message,
      };
    } catch (error) {
      console.error('Error handling Discord OAuth callback:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Discord OAuth 콜백 처리에 실패했습니다.'
      };
    }
  }

  /**
   * Discord 연동 상태 확인
   */
  async checkDiscordIntegration(
    userId: string,
    projectId: string
  ): Promise<{ connected: boolean; integration?: DiscordIntegration }> {
    try {
      const { data, error } = await this.supabase
        .from('discord_integrations')
        .select(`
          *,
          discord_workspaces (
            guild_name,
            icon
          )
        `)
        .eq('user_id', userId)
        .eq('project_id', projectId)
        .maybeSingle();

      if (error || !data) {
        return { connected: false };
      }

      return {
        connected: true,
        integration: {
          id: data.id,
          userId: data.user_id,
          projectId: data.project_id,
          guildId: data.guild_id,
          guildName: data.discord_workspaces?.guild_name,
          authorizedUserId: data.authorized_user_id,
          createdAt: data.created_at,
          updatedAt: data.updated_at,
        },
      };
    } catch (error) {
      console.error('Error checking Discord integration:', error);
      return { connected: false };
    }
  }

  /**
   * Discord 연동 해제
   */
  async disconnectDiscord(
    userId: string,
    projectId: string
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const { error } = await this.supabase
        .from('discord_integrations')
        .delete()
        .eq('user_id', userId)
        .eq('project_id', projectId);

      if (error) {
        return {
          success: false,
          error: error.message || 'Discord 연동 해제에 실패했습니다.'
        };
      }

      return { success: true };
    } catch (error) {
      console.error('Error disconnecting Discord:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Discord 연동 해제에 실패했습니다.'
      };
    }
  }

  /**
   * Discord 액세스 토큰 가져오기 (내부 헬퍼 메서드)
   */
  private async getDiscordAccessToken(
    userId: string,
    projectId: string
  ): Promise<string | null> {
    try {
      const { data, error } = await this.supabase
        .from('discord_integrations')
        .select('authorized_user_access_token')
        .eq('user_id', userId)
        .eq('project_id', projectId)
        .maybeSingle();

      if (error || !data) {
        return null;
      }

      return data.authorized_user_access_token;
    } catch (error) {
      console.error('Error getting Discord access token:', error);
      return null;
    }
  }
}